import { Component, ViewEncapsulation, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Util } from 'src/shared/util/util';
import { WindowService } from 'src/shared/providers/window.service';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Auth } from 'src/providers/auth/auth';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';

@Component({
    selector: 'tidy-onboarding-modal',
    templateUrl: './onboarding-modal.component.html',
    styleUrls: ['./onboarding-modal.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class OnboardingModalComponent implements OnInit, OnDestroy {
  private flowChangedSubscription: Subscription;
  private pageChangedSubscription: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    public onboardingProvider: OnboardingProvider,
    private util: Util,
    public windowService: WindowService,
    private storage: TidyStorage,
    private auth: Auth,
    private authModalService: AuthModalService,
    private aboutThisPageService: AboutThisPageService
  ) {
    this.flowChangedSubscription = this.onboardingProvider.flowChanged$.pipe(skip(1)).subscribe(() => {
      this.onboardingProvider.setOnboardingFlowText();
      this.cdr.detectChanges();
    });
    this.pageChangedSubscription = this.onboardingProvider.pageChanged$.pipe(skip(1)).subscribe(() => {
      this.resetScrollPosition();
      this.cdr.detectChanges();
    });
  }

  async ngOnInit() {
    if (!this.auth.isAuthenticated() || this.authModalService.isOpen()) {
      return;
    }
    this.onboardingProvider.loaded = false;
    await this.onboardingProvider.refreshOnboardingContent();
    if (this.onboardingProvider.flow === 'goals') {
      this.onboardingProvider.previousOnboardingFlow = null;
    }
    if (this.onboardingProvider.flow !== 'goals' && this.onboardingProvider.flow !== 'pickYourGoals') {
      this.onboardingProvider.loaded = true;
    }
  }

  private resetScrollPosition() {
    const contentArea = document.querySelector('.onboarding-content-area, .onboarding-content-area-mobile');
    if (contentArea) {
      contentArea.scrollTop = 0;
    }
  }

  onOverlayClick(_event: MouseEvent) {
    if (this.onboardingProvider.page === 'pickYourGoals') {
      return;
    }
    if (this.onboardingProvider.showedPrimaryModalOnFirstLoad && !this.onboardingProvider.previousOnboardingFlow) {
      return this.onboardingProvider.showOnboarding = false;
    }
    if (this.onboardingProvider.previousOnboardingFlow) {
      this.onboardingProvider.flow = this.onboardingProvider.previousOnboardingFlow;
      this.onboardingProvider.page = this.onboardingProvider.previousOnboardingFlow;
    }
    this.onboardingProvider.previousOnboardingFlow = null;
    this.onboardingProvider.refreshOnboardingContent();
    this.cdr.detectChanges();
  }

  private readonly simpleDismissFlows = ['messageAutomation', 'bills'];

  async handleButtonClick() {
    if (this.simpleDismissFlows.includes(this.onboardingProvider.flow)) {
      await this.simpleDismiss();
    } else {
      this.goToPrimaryModal();
    }
  }

  async simpleDismiss() {
    try {
      const flow = this.onboardingProvider.flow;
      // For metadata-based flows, save to aboutThisPage data
      if (flow === 'bills' || flow === 'messageAutomation') {
        await this.aboutThisPageService.dismissAboutThisPage(flow);
      } else {
        await this.onboardingProvider.markGoalAsCompleted(flow);
      }
      this.onboardingProvider.setShowOnboardingOnPage(false);
      this.onboardingProvider.setShowOnboardingInHeader(true);
    } catch (error) {
      this.util.showError('Error completing onboarding');
    }
  }

  goToPrimaryModal() {
    this.onboardingProvider.showPrimaryModal = true;
    this.cdr.detectChanges();
  }

  goBack() {
    this.onboardingProvider.page = this.onboardingProvider.priorPages[this.onboardingProvider.priorPages.length - 1];
    this.onboardingProvider.priorPages.pop();
    if (this.onboardingProvider.priorPages.length === 0) {
      this.onboardingProvider.showBackButton = false;
    }
  }

  openScheduleCallModal() {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  async closeModal() {
    await this.storage.delete('onboardingFlow');
    if (this.onboardingProvider.showedPrimaryModalOnFirstLoad) {
      this.onboardingProvider.setShowOnboardingInDashboard(true);
      this.onboardingProvider.showOnboarding = false;
    } else {
      this.onboardingProvider.showPrimaryModal = false;
    }
  }

  ngOnDestroy() {
    if (this.flowChangedSubscription) {
      this.flowChangedSubscription.unsubscribe();
    }
    if (this.pageChangedSubscription) {
      this.pageChangedSubscription.unsubscribe();
    }
  }

}